import { CheckCircle, XCircle, AlertTriangle, Loader2, Circle, ChevronDown, ChevronUp, Clock } from 'lucide-react'
import { cn } from '@trafiflow/shared-ui'
import type { AdGenStageStatus, StageStatus } from '@/services/ad-generator/types'
import { useState } from 'react'

interface StageCardProps {
  stage: AdGenStageStatus
  index: number
  isActive: boolean
  onViewDetail?: () => void
}

const statusConfig: Record<StageStatus, { icon: typeof CheckCircle; color: string; bgColor: string }> = {
  completed: { icon: CheckCircle, color: 'text-emerald-500', bgColor: 'bg-emerald-500/10' },
  degraded: { icon: AlertTriangle, color: 'text-amber-500', bgColor: 'bg-amber-500/10' },
  running: { icon: Loader2, color: 'text-blue-500', bgColor: 'bg-blue-500/10' },
  failed: { icon: XCircle, color: 'text-red-500', bgColor: 'bg-red-500/10' },
  skipped: { icon: Circle, color: 'text-tf-text-muted', bgColor: 'bg-tf-bg-muted' },
  pending: { icon: Circle, color: 'text-tf-text-muted', bgColor: 'bg-tf-bg-muted' },
}

export default function StageCard({ stage, index, isActive, onViewDetail }: StageCardProps) {
  const [expanded, setExpanded] = useState(false)
  const config = statusConfig[stage.status]
  const Icon = config.icon

  const durationSec = stage.duration_ms ? (stage.duration_ms / 1000).toFixed(1) : null

  return (
    <div
      className={cn(
        'rounded-lg border transition-all duration-300',
        isActive ? 'border-blue-500/50 bg-blue-500/5 shadow-sm' : 'border-tf-border bg-tf-bg-card',
        stage.status === 'failed' && 'border-red-500/30',
      )}
    >
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        {/* Stage number */}
        <span className={cn(
          'flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium',
          config.bgColor, config.color,
        )}>
          {index + 1}
        </span>

        {/* Status icon */}
        <Icon className={cn(
          'w-4 h-4 flex-shrink-0',
          config.color,
          stage.status === 'running' && 'animate-spin',
        )} />

        {/* Stage name */}
        <span className={cn(
          'flex-1 text-sm font-medium',
          stage.status === 'pending' ? 'text-tf-text-muted' : 'text-tf-text',
        )}>
          {stage.display_name}
        </span>

        {/* Timing */}
        {durationSec && (
          <span className="text-xs text-tf-text-muted flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {durationSec}s
          </span>
        )}
        {stage.status === 'running' && (
          <span className="text-xs text-blue-500 animate-pulse">
            ~{stage.eta_seconds}s
          </span>
        )}

        {/* Expand toggle */}
        {(stage.insight || stage.status !== 'pending') && (
          expanded ? <ChevronUp className="w-4 h-4 text-tf-text-muted" /> : <ChevronDown className="w-4 h-4 text-tf-text-muted" />
        )}
      </div>

      {/* Expanded content */}
      {expanded && stage.insight && (
        <div className="px-4 pb-3 pt-0">
          <p className="text-xs text-tf-text-muted pl-[52px]">{stage.insight}</p>
          {onViewDetail && (stage.status === 'completed' || stage.status === 'degraded') && (
            <button
              onClick={(e) => { e.stopPropagation(); onViewDetail() }}
              className="ml-[52px] mt-2 text-xs text-blue-500 hover:text-blue-400 underline"
            >
              View artifact details
            </button>
          )}
        </div>
      )}
    </div>
  )
}
